import { useTranslation } from 'react-i18next'

import trashIcon from '@assets/images/trash-icon.svg'

import {
  ArrayFieldModel,
  CheckboxFieldModel,
  DateFieldModel,
  FieldType,
  FormField,
  GridTextFieldModel,
  RadioFieldModel,
  SectionFieldModel,
  SelectEditableFieldModel,
  SelectFieldModel,
  SignatureFieldModel,
  TextFieldModel,
} from '@types'

import { Input } from '@components/Input'

import { FormFieldCard, FormFieldCardProps } from '../FormFieldCard'

import { MenuFormArrayModels } from '../MenuFormArrayModels'

import { FormFieldTextContent } from '../FormFieldText'
import { FormFieldRadioContent } from '../FormFieldRadio'
import { FormFieldCheckboxContent } from '../FormFieldCheckbox'
import { FormFieldSelectContent } from '../FormFieldSelect'
import { FormFieldSelectEditableContent } from '../FormFieldSelectEditable'
import { FormFieldDateContent } from '../FormFieldDate'
import { FormFieldSignatureContent } from '../FormFieldSignature'
import { FormFieldGridTextContent } from '../FormFieldGridText'

import {
  ButtonContainer,
  Container,
  FieldContainer,
  FieldContent,
  FieldLabel,
  FieldsContainer,
  Row,
} from './FormFieldArray.styled'
import { setRequiredOnArrayFields } from '../../FormBuilder.helper'

type ActionsProps = Pick<
  FormFieldCardProps,
  'canDown' | 'canUp' | 'onDelete' | 'onDuplicate' | 'onUp' | 'onDown' | 'hideRequired'
>

type FormFieldArrayProps = {
  field: ArrayFieldModel
  sections: SectionFieldModel[]
  projectId: string
  onChange: (value: Partial<ArrayFieldModel>) => void
  onAddField: (id: string, type: FieldType) => void
  onRemoveField: (arrayFieldId: string, fieldId: string) => void
  onDeleteOption: (arrayFieldId: string, fieldId: string, optionId: string) => void
} & ActionsProps

export const FormFieldArray: React.FC<FormFieldArrayProps> = ({
  field,
  sections,
  projectId,
  onChange,
  onAddField,
  onRemoveField,
  onDeleteOption,
  ...actionsProps
}: FormFieldArrayProps) => {
  const { t } = useTranslation()

  const handleAddField = (type: FieldType) => {
    onAddField(field.id, type)
  }

  const handleDeleteOption = (fieldId: string, optionId: string) => {
    onDeleteOption(field.id, fieldId, optionId)
  }

  const renderField = (item: FormField, index: number) => {
    switch (item.type) {
      case FieldType.Text:
        return (
          <FormFieldTextContent
            field={item as TextFieldModel}
            onChange={(value) => {
              const [col] = field.cols
              const newFields = [...col.fields]
              newFields[index] = { ...newFields[index], ...value } as TextFieldModel

              onChange({ cols: [{ ...col, fields: newFields }] })
            }}
          />
        )

      case FieldType.Radio:
        return (
          <FormFieldRadioContent
            field={item as RadioFieldModel}
            projectId={projectId}
            sections={sections}
            onChange={(value) => {
              const [col] = field.cols
              const newFields = [...col.fields]
              newFields[index] = { ...newFields[index], ...value } as RadioFieldModel

              onChange({ cols: [{ ...col, fields: newFields }] })
            }}
            onDeleteOption={handleDeleteOption}
          />
        )

      case FieldType.Checkbox:
        return (
          <FormFieldCheckboxContent
            field={item as CheckboxFieldModel}
            projectId={projectId}
            onChange={(value) => {
              const [col] = field.cols
              const newFields = [...col.fields]
              newFields[index] = { ...newFields[index], ...value } as CheckboxFieldModel

              onChange({ cols: [{ ...col, fields: newFields }] })
            }}
            onDeleteOption={handleDeleteOption}
          />
        )

      case FieldType.Select:
        return (
          <FormFieldSelectContent
            field={item as SelectFieldModel}
            projectId={projectId}
            sections={sections}
            onChange={(value) => {
              const [col] = field.cols
              const newFields = [...col.fields]
              newFields[index] = { ...newFields[index], ...value } as SelectFieldModel

              onChange({ cols: [{ ...col, fields: newFields }] })
            }}
            onDeleteOption={handleDeleteOption}
          />
        )

      case FieldType.SelectEditable:
        return (
          <FormFieldSelectEditableContent
            field={item as SelectEditableFieldModel}
            projectId={projectId}
            sections={sections}
            onChange={(value) => {
              const [col] = field.cols
              const newFields = [...col.fields]
              newFields[index] = { ...newFields[index], ...value } as SelectEditableFieldModel

              onChange({ cols: [{ ...col, fields: newFields }] })
            }}
            onDeleteOption={handleDeleteOption}
          />
        )

      case FieldType.Date:
        return (
          <FormFieldDateContent
            field={item as DateFieldModel}
            onChange={(value) => {
              const [col] = field.cols
              const newFields = [...col.fields]
              newFields[index] = { ...newFields[index], ...value } as DateFieldModel

              onChange({ cols: [{ ...col, fields: newFields }] })
            }}
          />
        )

      case FieldType.Signature:
        return (
          <FormFieldSignatureContent
            field={item as SignatureFieldModel}
            onChange={(value) => {
              const [col] = field.cols
              const newFields = [...col.fields]
              newFields[index] = { ...newFields[index], ...value } as SignatureFieldModel

              onChange({ cols: [{ ...col, fields: newFields }] })
            }}
          />
        )

      case FieldType.GridText:
        return (
          <FormFieldGridTextContent
            field={item as GridTextFieldModel}
            projectId={projectId}
            onChange={(value) => {
              const [col] = field.cols
              const newFields = [...col.fields]
              newFields[index] = { ...newFields[index], ...value } as GridTextFieldModel

              onChange({ cols: [{ ...col, fields: newFields }] })
            }}
          />
        )

      default:
        return null
    }
  }

  return (
    <FormFieldCard
      name={field.label}
      required={field.required}
      onChangeRequired={(value) => {
        const updatedArrayField = setRequiredOnArrayFields(field, value)
        onChange(updatedArrayField)
      }}
      {...actionsProps}
    >
      <Container>
        <Input
          type="text"
          required
          label={t('modules_project-form-models_components_FormFieldArray_question_label')}
          value={field.label}
          onChange={(value) => onChange({ label: value })}
        />

        {field.cols.length > 0 && (
          <FieldsContainer>
            {field.cols[0].fields.map((item, index: number) => (
              <FieldContainer key={item.id}>
                <Row>
                  <FieldLabel>
                    {t('modules_project-form-models_components_FormFieldArray_col_label', {
                      number: index + 1,
                    })}
                  </FieldLabel>
                  {field.cols[0].fields.length > 1 && (
                    <ButtonContainer
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()

                        onRemoveField(field.id, item.id)
                      }}
                    >
                      <img src={trashIcon} />
                    </ButtonContainer>
                  )}
                </Row>
                <FieldContent>{renderField(item, index)}</FieldContent>
              </FieldContainer>
            ))}
          </FieldsContainer>
        )}
        <FieldContainer>
          {field.cols.length > 0 && (
            <FieldLabel>
              {t('modules_project-form-models_components_FormFieldArray_col_label', {
                number: field.cols[0]?.fields?.length + 1,
              })}
            </FieldLabel>
          )}
          <MenuFormArrayModels onSelect={handleAddField} />
        </FieldContainer>
      </Container>
    </FormFieldCard>
  )
}
